// UnifiedLogger.jsx
import { useRef, useCallback } from "react";

class UnifiedLogger {
    constructor() {
        this.isEnabled = import.meta.env.VITE_BHASHINI_LOGGING_ENABLED !== "false";
        this.logLevel = this.getLogLevel();
        this.environment = import.meta.env.MODE || "development";
        this.appName = import.meta.env.VITE_APP_NAME || "BhashiniApp";
        this.version = import.meta.env.VITE_APP_VERSION || "1.0.0";

        this.sessionId = this.generateSessionId();
        this.logBuffer = [];
        this.maxBufferSize = 500;

        this.metrics = {
            totalLogs: 0,
            errorCount: 0,
            warningCount: 0,
            sessionStartTime: Date.now(),
        };

        this.performanceMarks = new Map();

        if (this.environment === "development") {
            console.info(
                `${this.appName} Logger v${this.version} - Session: ${this.sessionId}`
            );
        }
    }

    // --- Utils ---
    generateSessionId() {
        return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    }

    getLogLevel() {
        const envLevel = import.meta.env.VITE_LOG_LEVEL || "INFO";
        const levels = { ERROR: 3, WARNING: 4, INFO: 6, DEBUG: 7 };
        return levels[envLevel.toUpperCase()] || 6;
    }

    // --- Performance ---
    startPerformance(op) {
        this.performanceMarks.set(op, performance.now());
    }
    endPerformance(op) {
        const start = this.performanceMarks.get(op);
        if (!start) return null;
        const duration = performance.now() - start;
        this.performanceMarks.delete(op);
        this.info("PERF", `${op} took ${Math.round(duration)}ms`);
        return duration;
    }

    // --- Core Logging ---
    log(level, category, message, data = null) {
        if (!this.isEnabled) return;
        const entry = {
            id: `${this.sessionId}-${Date.now()}`,
            timestamp: new Date().toISOString(),
            level,
            category,
            message,
            data,
        };

        this.metrics.totalLogs++;
        if (level === "ERROR") this.metrics.errorCount++;
        if (level === "WARNING") this.metrics.warningCount++;

        this.logBuffer.push(entry);
        if (this.logBuffer.length > this.maxBufferSize) {
            this.logBuffer.shift();
        }

        this.outputToConsole(entry);
        return entry;
    }

    safeStringify(obj) {
        const seen = new WeakSet();
        return JSON.stringify(obj, (key, value) => {
            if (typeof value === "object" && value !== null) {
                if (seen.has(value)) return "[Circular]";
                seen.add(value);
            }
            return value;
        });
    }

    outputToConsole({ level, category, message, data, timestamp }) {
        const time = new Date(timestamp).toLocaleTimeString();
        const payload = data ? ` | data=${this.safeStringify(data)}` : "";
        const msg = `${time} [${level}] ${category} - ${message}${payload}`;

        if (level === "CRITICAL" || level === "ERROR") console.error(msg);
        else if (level === "WARNING") console.warn(msg);
        else if (level === "INFO") console.info(msg);
        else console.debug(msg);
    }

    // --- Shortcuts ---
    error(c, m, d) { return this.log("ERROR", c, m, d); }
    warning(c, m, d) { return this.log("WARNING", c, m, d); }
    info(c, m, d) { return this.log("INFO", c, m, d); }
    debug(c, m, d) { return this.log("DEBUG", c, m, d); }
    critical(c, m, d) {
        return this.log("CRITICAL", c, m, d);
    }

    // --- Summary ---
    printSummary() {
        return {
            ...this.metrics,
            bufferSize: this.logBuffer.length,
            uptimeSec: Math.round((Date.now() - this.metrics.sessionStartTime) / 1000),
        };
    }
}

// Singleton
const logger = new UnifiedLogger();

// React Hook
export function useLogger(componentName = "Component", isEnabled = false) {
    const compRef = useRef(componentName);

    const trackPerformance = useCallback(async (op, fn) => {
        const opName = `${compRef.current}.${op}`;
        logger.startPerformance(opName);
        try {
            return await fn();
        } catch (err) {
            logger.error("COMPONENT_ERROR", `${opName} failed`, {
                error: err?.message,
            });
            throw err;
        } finally {
            logger.endPerformance(opName);
        }
    }, []);

    return {
        logError: (c, m, d) => isEnabled ? logger.error(`${compRef.current}.${c}`, m, d) : null,
        logWarning: (c, m, d) => isEnabled ? logger.warning(`${compRef.current}.${c}`, m, d) : null,
        logInfo: (c, m, d) => isEnabled ? logger.info(`${compRef.current}.${c}`, m, d) : null,
        logDebug: (c, m, d) => isEnabled ? logger.debug(`${compRef.current}.${c}`, m, d) : null,
        trackPerformance,
        printSummary: () => isEnabled ? logger.printSummary() : null,
    };
}

export default logger;


export function getLogger(componentName = "Global", isEnabled = true) {
    return {
        logError: (c, m, d) => isEnabled ? logger.error(`${componentName}.${c}`, m, d) : null,
        logWarning: (c, m, d) => isEnabled ? logger.warning(`${componentName}.${c}`, m, d) : null,
        logInfo: (c, m, d) => isEnabled ? logger.info(`${componentName}.${c}`, m, d) : null,
        logDebug: (c, m, d) => isEnabled ? logger.debug(`${componentName}.${c}`, m, d) : null,
        printSummary: () => isEnabled ? logger.printSummary() : null,
    };
}